import { useEffect, useState } from 'react';
import { FileText, Film, X } from 'lucide-react';

import { FilePreviewModal, type PreviewSource } from '@/components/ui/FilePreviewModal';
import { SecureImage } from '@/components/ui/SecureImage';
import { formatFileSize } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface AttachmentTileProps {
  name: string;
  /** Bytes. Null or absent when a stored file's size is not known. */
  size?: number | null;
  /** MIME type when known. Without it the extension decides the tile's look. */
  type?: string;
  /** A file chosen on this device and not uploaded yet — previewed from memory. */
  file?: File | null;
  /** A stored file's API path — previewed by fetching it with the session. */
  path?: string;
  /**
   * The corner X. Exactly what the field's remove did before the tile existed;
   * absent leaves the tile without one (a read-only form, a stored file that
   * can only be replaced).
   */
  onRemove?: () => void;
  disabled?: boolean;
}

type Kind = 'image' | 'video' | 'file';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov'];

/**
 * Picture, video or anything else — what the tile draws.
 *
 * Raster images only count as pictures: an SVG drawn as a thumbnail is harmless
 * in an `<img>`, but the preview will not open one (see `FilePreviewModal`), and
 * a tile that promises a picture it then refuses to show is worse than an icon.
 */
function kindOf(name: string, type?: string): Kind {
  const mime = type ?? '';
  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  if (['image/png', 'image/jpeg', 'image/webp'].includes(mime) || IMAGE_EXTENSIONS.includes(extension)) {
    return 'image';
  }
  if (mime.startsWith('video/') || VIDEO_EXTENSIONS.includes(extension)) return 'video';
  return 'file';
}

/**
 * One attached file as a square: a thumbnail for a picture, a first frame for a
 * video chosen just now, an icon for the rest — with its remove button on the
 * corner and the whole tile opening it in a preview.
 *
 * Shared by every attachment field (`FileUpload`, the daily report's list) so a
 * file looks and behaves the same wherever it was attached (CLAUDE.md §3). It
 * only draws: removing calls the caller's own handler, unchanged.
 *
 * A stored video is not previewed — fetching a recording of several hundred MB
 * to look at it would be a download in all but name — so its tile is not a
 * button.
 */
export function AttachmentTile({
  name,
  size = null,
  type,
  file = null,
  path,
  onRemove,
  disabled = false,
}: AttachmentTileProps) {
  const kind = kindOf(name, file?.type ?? type);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewSource | null>(null);

  // A thumbnail of a file still in memory: an object URL, revoked with the tile.
  useEffect(() => {
    if (!file || kind === 'file') return;
    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setLocalUrl(null);
    };
  }, [file, kind]);

  const source: PreviewSource | null = file
    ? { file }
    : path && kind !== 'video'
      ? { path }
      : null;

  const icon =
    kind === 'video' ? (
      <Film className="h-7 w-7 text-ink-3" aria-hidden />
    ) : (
      <FileText className="h-7 w-7 text-ink-3" aria-hidden />
    );

  const face =
    kind === 'image' && localUrl ? (
      <img src={localUrl} alt="" className="h-full w-full object-cover" />
    ) : kind === 'image' && path ? (
      <SecureImage path={path} alt="" className="h-full w-full object-cover" fallback={icon} />
    ) : kind === 'video' && localUrl ? (
      // `#t=0.1` asks for an early frame rather than a black first one.
      <video src={`${localUrl}#t=0.1`} muted preload="metadata" className="h-full w-full object-cover" />
    ) : (
      icon
    );

  const faceClass =
    'grid h-24 w-24 place-items-center overflow-hidden rounded-xl border border-line bg-surface-2 shadow-2xs';

  return (
    <div className="flex w-24 flex-col gap-1">
      <div className="relative">
        {source ? (
          <button
            type="button"
            onClick={() => setPreview(source)}
            aria-label={`Preview ${name}`}
            title={name}
            className={cn(
              faceClass,
              'cursor-pointer transition-colors hover:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
            )}
          >
            {face}
          </button>
        ) : (
          <div className={faceClass} title={name}>
            {face}
          </div>
        )}

        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Remove ${name}`}
            className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full border border-line bg-surface text-ink-3 shadow-sm transition-colors hover:text-crit focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>

      <p className="truncate text-2xs font-semibold text-ink" title={name}>
        {name}
      </p>
      {size !== null && size > 0 && (
        <p className="-mt-1 text-2xs tabular-nums text-ink-3">{formatFileSize(size)}</p>
      )}

      <FilePreviewModal source={preview} fileName={name} onClose={() => setPreview(null)} />
    </div>
  );
}
